<template>
  <v-card>
    <Subheader title="Approval Level System Default Configurations" v-model="approvalSearch" search="Approval Levels">
      <v-btn color="add" @click="addApprovalLevel" v-if="!readonly">
        <v-icon class="mr-1">add</v-icon>
        {{ $t('Button.Add') }}
      </v-btn>
    </Subheader>
    <v-card-text>
      <v-alert outline color="info" :value="true" icon="info">
        Configure system-wide default approval levels configurations. Tenant configurations take presidence over Default configurations. Default configurations are used when Tenant configuration values are not present. For more information on how configurations work, see tenant configuration table information header.
      </v-alert>
      <v-data-table :headers="headers" :items="systemDefaultApprovalLevelConfigurations" :pagination.sync="pagination" :search="approvalSearch" :loading="isLoading">
        <template v-slot:items="props">
          <td class="text-xs-left">{{props.item.MappedValue ? props.item.MappedValue : $t('ContinuousApprovalLevelManage.default')}}</td>
          <td class="text-xs-left">{{props.item.OutputText}}</td>
          <td class="text-xs-left">{{props.item.OutputDescription}}</td>
          <td class="text-xs-left" v-if="!readonly">
            <EditButton small @click="updateApprovalLevel(props.item)">Edit</EditButton>
            <DeleteButton small @click="deleteApprovalLevel(props.item)" :loading="isBeingDeleted(props.item)">Delete</DeleteButton>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
    <UpsertApprovalLevelForm v-model="upsertApprovalLevelFormModal" :approvalLevelConfiguration="approvalLevelConfigurationToUpdate" :continuousDatabaseType="continuousDatabaseType" system-default />
    <ConfirmationDialog2 ref="confirmationDialog" />
  </v-card>
</template>
<script>
  import UpsertApprovalLevelForm from '@/components/ContinuousDataOutputManage/ContinuousApprovalLevels/Forms/UpsertApprovalLevelForm'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const app = createNamespacedHelpers('app')
  const approvalLevels = createNamespacedHelpers('approvalLevels')
  export default {
    components: {
      UpsertApprovalLevelForm,
    },
    props: {
      continuousDatabaseType: {
        type: String,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        approvalSearch: '',
        availableHeaders: {
          databaseValue: {
            text: 'Continuous Database Value',
            value: 'MappedValue',
            align: 'left',
          },
          outputText: {
            text: 'Output Text',
            value: 'OutputText',
            align: 'left',
          },
          outputDescription: {
            text: 'Output Description',
            value: 'OutputDescription',
            align: 'left',
          },
          actions: {
            text: 'Actions',
            value: '',
            align: 'left',
            sortable: false,
          },
        },
        pagination: {
          rowsPerPage: 10,
        },
        upsertApprovalLevelFormModal: false,
        approvalLevelConfigurationToUpdate: null,
        approvalLevelConfigurationToDelete: null,
      }
    },
    computed: {
      ...approvalLevels.mapState(['systemDefaultApprovalLevelConfigurations', 'isLoading']),
      headers() {
        let headers = [
          this.availableHeaders.databaseValue,
          this.availableHeaders.outputText,
          this.availableHeaders.outputDescription,
        ]
        if (!this.readonly) {
          headers.push(this.availableHeaders.actions)
        }
        return headers
      },
    },
    watch: {
      loading: async function() {
        if (!this.loading && !!this.continuousDatabaseType) {
          await this.queryConfigurations()
        }
      },
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...approvalLevels.mapActions(['getSystemDefaultApprovalLevelConfigurations', 'deleteSystemDefaultApprovalLevelConfiguration']),
      async queryConfigurations() {
        if (this.continuousDatabaseType) {
          await this.getSystemDefaultApprovalLevelConfigurations({ continuousDatabaseType: this.continuousDatabaseType })
        }
      },
      addApprovalLevel() {
        this.approvalLevelConfigurationToUpdate = null
        this.upsertApprovalLevelFormModal = true
      },
      updateApprovalLevel(approvalLevelConfiguration) {
        this.approvalLevelConfigurationToUpdate = approvalLevelConfiguration
        this.upsertApprovalLevelFormModal = true
      },
      async deleteApprovalLevel(approvalLevelConfiguration) {
        const isDefaultConfiguration = !approvalLevelConfiguration.MappedValue
        const params = {
          title: this.$t('ContinuousApprovalLevelManage.deleteSystemDefaultApprovalLevelConfirmation'),
          text: isDefaultConfiguration ?
            this.$t('ContinuousApprovalLevelManage.deleteSystemDefaultApprovalLevelTextDefault') : this.$t('ContinuousApprovalLevelManage.deleteSystemDefaultApprovalLevelTextNonDefault', { msg: approvalLevelConfiguration.MappedValue }),
          confirmText: this.$t('Button.Delete'),
          cancelText: this.$t('Button.Cancel'),
          color: 'error',
          icon: 'delete_forever',
        }
        if (await this.$refs.confirmationDialog.confirm(params)) {
          this.approvalLevelConfigurationToDelete = approvalLevelConfiguration
          try {
            await this.deleteSystemDefaultApprovalLevelConfiguration({ configuration: approvalLevelConfiguration, continuousDatabaseType: this.continuousDatabaseType })
            await this.getSystemDefaultApprovalLevelConfigurations({ continuousDatabaseType: this.continuousDatabaseType })
            this.setSnackbar({
              icon: 'done',
              color: 'success',
              text: this.$t('ContinuousApprovalLevelManage.deleteSuccessMessage'),
            })
          } catch (ex) {
            const exResponse = ex && ex.response && ex.response.data
            const msg = exResponse && (exResponse.ExceptionMessage || exResponse.Message)
            this.setSnackbar({
              icon: 'error',
              text: this.$t('ContinuousApprovalLevelManage.deleteErrorMessage', { msg }),
              color: 'error',
            })
          } finally {
            this.approvalLevelConfigurationToDelete = null
          }
        }
      },
      isBeingDeleted(approvalLevelConfiguration) {
        if (!this.approvalLevelConfigurationToDelete) {
          return false
        }
        return this.approvalLevelConfigurationToDelete.Id == approvalLevelConfiguration.Id
      },
    },
    async created() {
      await this.queryConfigurations()
    },
  }
</script>